import cloneDeep from "lodash/cloneDeep";
import {
  EMPTY_ROUNDING,
  type DerivedTable,
  type DerivedTableColumn,
  type DerivedTableFilter,
  type DerivedTableOrdering,
  type DerivedTableRounding,
  type DerivedTableTab,
} from "@packages/commons";

export type {
  ColumnDataType,
  Comparator,
  DerivedTable,
  DerivedTableColumn,
  DerivedTableFilter,
  DerivedTableOrdering,
  DerivedTableRounding,
  DerivedTableTab,
  DerivedTableTabRow,
} from "@packages/commons";
export { COLUMN_DATA_TYPES, COMPARATORS, EMPTY_ROUNDING } from "@packages/commons";

/**
 * Fills in `rounding`/`filters`/`ordering` for a table loaded from storage before those fields existed.
 * Always route a `DerivedTable` coming from outside this feature (the server, local storage)
 * through this before using it, so the rest of the editor can treat those fields as required.
 */
export const normalizeDerivedTable = (
  table: DerivedTable,
): Required<Pick<DerivedTable, "rounding" | "filters" | "ordering">> & DerivedTable => ({
  ...table,
  rounding: table.rounding ?? EMPTY_ROUNDING,
  filters: table.filters ?? [],
  ordering: table.ordering ?? [],
});

/** The user-editable parts of a derived table. */
export interface Draft {
  name: string;
  entity: string | null;
  columns: DerivedTableColumn[];
  tabs: DerivedTableTab[];
  rounding: DerivedTableRounding;
  filters: DerivedTableFilter[];
  ordering: DerivedTableOrdering[];
}

export const toDraft = (v: DerivedTable): Draft => {
  const normalized = normalizeDerivedTable(v);
  return {
    name: normalized.name,
    entity: normalized.entity,
    columns: normalized.columns,
    tabs: normalized.tabs,
    rounding: normalized.rounding,
    filters: normalized.filters,
    ordering: normalized.ordering,
  };
};

export const draftEqual = (a: Draft, b: Draft) => JSON.stringify(a) === JSON.stringify(b);

/** Whether a draft has changes worth saving - a table can't be saved without a name. */
export const isDraftDirty = (draft: Draft, saved: DerivedTable) => !draftEqual(draft, toDraft(saved)) && draft.name.trim().length > 0;

/** A minimal shape of the parts of a Relationship record needed to list entities. */
export interface EntityOption {
  label: string;
  value: string;
}

export type DerivedTableSortDir = "default" | "a-z" | "z-a";

export const sortDerivedTables = (tables: DerivedTable[], dir: DerivedTableSortDir): DerivedTable[] => {
  if (dir === "default") return tables;

  return cloneDeep(tables).sort((a, b) => {
    const aName = a.name.toLowerCase();
    const bName = b.name.toLowerCase();

    if (dir === "a-z") {
      return aName > bName ? 1 : -1;
    }

    return aName < bName ? 1 : -1;
  });
};
